<script>
import { onMount } from "svelte";
import { writable } from 'svelte/store';
import { getPieces, postSelectedData } from "../http.js";
import {
    createObject,
    employeeChoices,
    unselectPiece
} from "../serializeEmployeeChoices.js";
import { taskOwners, selectFreeTasks } from "../selection.js";

import Notification from "./Notification.svelte";
import ConfirmationView from "./ConfirmationView.svelte";
import Chevron from "./Chevron.svelte";
import ThemeToggle from "./ThemeToggle.svelte";

let toggleLoader = true;
let showDialog = false;
let dialogType = undefined;
let dialogMessage = undefined;
let isSended = false;
let isLoadFailed = false;
let piecesList = writable([]);
let disableBtn = writable(true);
let introOpen = true;
let openPieces = {};
let owners = {};
let taskCount = 0;

$: assignedCount = Object.keys(owners).length;

const getResponse = async() => {
    try {
        const response = await getPieces();
        response.forEach(element => {
            Object.keys(element).map(k => element.name = element.name.trim());
        });
        piecesList.set(response);
        taskCount = countDistinctTasks(response);
        toggleLoader = false;

    } catch (error) {
        toggleLoader = false;
        showDialog = true;
        isLoadFailed = true;
        dialogType = "is-danger";
        dialogMessage = `Zaznaczone zadania zostały już przesłane do weryfikacji.`;
    };
};

const countDistinctTasks = (pieces) => {
    const names = new Set();
    pieces.forEach(piece => piece.tasks.forEach(task => names.add(task.name)));

    return names.size;
};

const sendSelectedData = async () => {
    try {
        await postSelectedData(employeeChoices);
        disableBtn = true;
        isSended = true;
    } catch (error) {
        showDialog = true;
        dialogType = "is-danger";
        dialogMessage = `Nie możemy wysłać Twoich wyborów, spróbuj ponownie.`;
        isSended = false;
    }
};

const blockSendBtn = () => {
    (employeeChoices.length === 0) ? disableBtn = true : disableBtn = false;
};

const sync = () => {
    owners = taskOwners(employeeChoices);
    blockSendBtn();
};

const heldBy = (piece, taskOwners) => {
    return piece.tasks.filter(task => taskOwners[task.task_id] === piece.piece_id).length;
};

const hasFreeTask = (piece, taskOwners) => {
    return piece.tasks.some(task => taskOwners[task.task_id] === undefined);
};

const isHeldBy = (task, piece, taskOwners) => taskOwners[task.task_id] === piece.piece_id;

const isClaimedElsewhere = (task, piece, taskOwners) => {
    const owner = taskOwners[task.task_id];

    return owner !== undefined && owner !== piece.piece_id;
};

const pieceName = (pieceId) => $piecesList.find(piece => piece.piece_id === pieceId).name;

const selectObject = (piece, task) => {
    createObject(piece, task);
    sync();
};

const selectAll = (piece) => {
    selectFreeTasks(piece);
    sync();
};

const unSelectAll = (piece) => {
    unselectPiece(piece);
    sync();
};

const togglePiece = (pieceId) => {
    openPieces[pieceId] = !openPieces[pieceId];
};

onMount(async () => {
    getResponse();
});
</script>

{#if toggleLoader}
    <div class="loader">
        <div class="loader__spinner" aria-hidden="true"></div>
        <p class="loader__text" role="status">Pobieramy Twoje zadania...</p>
    </div>
{:else}
    <header class="app-header">
        <div class="app-header__inner">
            <span class="app-header__brand">
                <img class="app-header__logo" src="/logo.png" alt="" width="32" height="32">
                Sarnolot
            </span>
            {#if !isSended && !isLoadFailed}
                <span class="app-header__progress">{assignedCount}/{taskCount} zadań</span>
            {/if}
            <ThemeToggle />
        </div>
    </header>

    {#if isSended}
        <ConfirmationView />
    {:else if isLoadFailed}
        <div class="state state--muted">
            <span class="state__icon" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 6 9 17l-5-5"/>
                </svg>
            </span>
            <p class="state__text">{dialogMessage}</p>
        </div>
    {:else}
        <main class="page">
            {#if showDialog}
                <Notification dialogType={dialogType} dialogMessage={dialogMessage}/>
            {/if}

            <h1 class="page__title">Miesięczne zestawienie prac autorskich</h1>
            <p class="page__subtitle">Umowa o pracę</p>

            <section class="panel intro" class:is-open={introOpen}>
                <button class="disclosure" aria-expanded={introOpen} on:click={() => introOpen = !introOpen}>
                    <span class="intro__label">
                        Co mam zrobić <span class="intro__duration">· 5-10 minut</span>
                    </span>
                    <span class="disclosure__hint">
                        {introOpen ? 'Zwiń' : 'Rozwiń'}
                        <Chevron />
                    </span>
                </button>

                {#if introOpen}
                    <div class="intro__body">
                        <p>
                            <strong>
                                Abyś mógł/mogła dostawać więcej pieniążków tytułem praw
                                autorskich, potrzebna jest dokumentacja potwierdzająca,
                                że w ostatnim miesiącu pracowałeś/aś nad utworami autorskimi.
                            </strong>
                            Niech Cię to nie wystraszy. Ta dokumentacja jest do użytku wewnętrznego,
                            nie trafia do księgowości i nie musi być totalnie rygorystycznie prowadzona.
                            Niemniej jednak jest nam potrzebna, gdybyśmy musieli wykazać kiedyś,
                            że faktycznie należy Ci się ulga podatkowa wynikająca z praw autorskich.
                        </p>

                        <p>
                            Zadanie jest proste. Poniżej znajduje się lista projektów
                            i zadań w których zalogowałeś/aś czas.
                        </p>

                        <p>
                            Zadania, w których wykonujesz pracę kreatywną,
                            czyli <strong>tworzysz coś nowego</strong>, przydzielasz do zdefiniowanych
                            dla projektu utworów autorskich. Czasami jest to cały projekt, czasami
                            jakiś moduł albo pojedyncza aplikacja wydzielona w dłuższym projekcie.
                        </p>

                        <p>
                            Dla każdego utworu wybierz wszystkie zadania kreatywne, nad którymi pracujesz.
                            Nie musisz przydzialać każdego taska, niektóre nie są pracą kreatywną.
                            Przykłady poniżej.
                        </p>

                        <p>
                            Czasami w projekcie może być więcej niż jeden utwór autorski.
                            Dopasuj zadania pod właściwy utwór.
                        </p>

                        <p>I to wszystko! Powinno Ci to razem zająć 5-10 minut.</p>

                        <p>Masz pytania? Pisz na kanale <strong>#sarnolot</strong> na Slacku.</p>

                        <div class="examples">
                            <div class="example example--ok">
                                <p class="example__title">To jest OK</p>
                                <ul class="example__list">
                                    <li>Nowa funkcja, moduł w kodzie</li>
                                    <li>Grafiki, makiety, szkice</li>
                                    <li>Interfejsy graficzne</li>
                                    <li>Architektura i prace koncepcyjne</li>
                                </ul>
                            </div>
                            <div class="example">
                                <p class="example__title">Tego nie zaznaczaj</p>
                                <ul class="example__list">
                                    <li>Testy</li>
                                    <li>Bugfiksy i refactoring</li>
                                    <li>Dokumentacja</li>
                                    <li>Uczestnictwo w spotkaniach</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                {/if}
            </section>

            <h2 class="page__section-title">Przypisz swoją pracę do dzieła</h2>

            <div class="pieces">
                {#each $piecesList as piece}
                    <section class="panel piece" class:is-open={openPieces[piece.piece_id]}>
                        <button class="piece__toggle"
                            aria-expanded={!!openPieces[piece.piece_id]}
                            aria-controls="panel-{piece.piece_id}"
                            on:click={() => togglePiece(piece.piece_id)}>
                            <Chevron />
                            <span class="piece__heading">
                                <span class="piece__eyebrow">Dzieło</span>
                                <span class="piece__name">{piece.name}</span>
                            </span>
                            <span class="piece__count" class:has-selection={heldBy(piece, owners) > 0}>
                                {heldBy(piece, owners)}
                            </span>
                        </button>

                        {#if openPieces[piece.piece_id]}
                            <div class="piece__panel" id="panel-{piece.piece_id}">
                                <div class="piece__actions">
                                    <button class="btn btn--secondary"
                                        disabled={!hasFreeTask(piece, owners)}
                                        on:click={() => selectAll(piece)}>Zaznacz wszystkie zadania</button>
                                    <button class="btn btn--ghost"
                                        disabled={heldBy(piece, owners) === 0}
                                        on:click={() => unSelectAll(piece)}>Odznacz wszystkie zadania</button>
                                </div>

                                <ul class="tasks">
                                    {#each piece.tasks as task}
                                        <li class="task" class:is-claimed={isClaimedElsewhere(task, piece, owners)}>
                                            <label class="task__label" for="{piece.piece_id}-{task.task_id}">
                                                <input type="checkbox" id="{piece.piece_id}-{task.task_id}"
                                                    checked={isHeldBy(task, piece, owners)}
                                                    disabled={isClaimedElsewhere(task, piece, owners)}
                                                    on:change={() => selectObject(piece, task)}>
                                                <span class="task__name">{task.name}</span>
                                            </label>
                                            {#if isClaimedElsewhere(task, piece, owners)}
                                                <p class="task__owner">Przypisane do dzieła: {pieceName(owners[task.task_id])}</p>
                                            {/if}
                                        </li>
                                    {/each}
                                </ul>
                            </div>
                        {/if}
                    </section>
                {/each}
            </div>
        </main>

        <div class="submit-bar">
            <div class="submit-bar__inner">
                <p class="submit-bar__status">
                    {assignedCount === 0
                        ? 'Nic jeszcze nie przypisano'
                        : `Przypisano ${assignedCount} z ${taskCount} zadań`}
                </p>
                <button class="btn btn--primary" on:click="{sendSelectedData}" disabled='{disableBtn}'>
                    Wyślij do akceptacji
                </button>
            </div>
        </div>
    {/if}
{/if}
